import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { UserX, Wallet, Globe2 } from 'lucide-react';
interface SlideBottleneckProps {
  onContinue: () => void;
}
const ISSUES = [{
  label: 'The people gap',
  title: 'Affiliates are understaffed',
  icon: UserX,
  desc: 'Local teams are sized for launch and growth brands.'
}, {
  label: 'The cost trap',
  title: 'Agencies are not sustainable here',
  icon: Wallet,
  desc: 'The gap between revenue and marketing cost approaches zero.'
}, {
  label: 'The distance gap',
  title: 'HQ cannot execute locally',
  icon: Globe2,
  desc: 'Global builds master content, not executing local market-ready campaigns.'
}];
export function SlideBottleneck({
  onContinue
}: SlideBottleneckProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [300, 800, 1300, 2000].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-12 max-w-7xl mx-auto" data-id="element-180" data-mp="1y7sfcnnt1mny">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-3 shrink-0" data-id="element-181" data-mp="236dh4ripgeei">
        <p className="text-rose-400 font-semibold tracking-widest uppercase text-sm" data-id="element-182" data-mp="2d5cdpbq74jl5">
          The Bottleneck
        </p>
        <h2 className="text-3xl md:text-5xl font-serif text-white" data-id="element-183" data-mp="1ffzlkr0wvxf6">
          Established brands don't reach their potential
        </h2>
      </motion.div>

      <div className="flex-1 min-h-0 flex flex-col justify-center max-w-6xl mx-auto w-full mt-6 space-y-5" data-id="element-184" data-mp="1pf8klyamw28e">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: stage >= 1 ? 1 : 0,
        y: stage >= 1 ? 0 : 12
      }} className="bg-rose-500/10 border border-rose-500/30 rounded-2xl px-7 py-5" data-id="element-185" data-mp="1u7rxsu5esvco">
          <p className="text-rose-400 text-[11px] font-semibold uppercase tracking-widest mb-2" data-id="element-186" data-mp="toop5bln9tud">
            The impact
          </p>
          <p className="text-white/90 text-base md:text-lg leading-relaxed" data-id="element-187" data-mp="op2d35hjyjn5">
            The real problem is not that these brands lack value.{' '}
            <span className="text-rose-400 font-semibold" data-id="element-188" data-mp="yhrd947nkb36">
              Nobody has the hands, the budget model or the local reach
            </span>{' '}
            — so they don't get the love and attention they deserve.
          </p>
        </motion.div>

        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: stage >= 2 ? 1 : 0
      }} className="text-lg text-white/60 !mt-6" data-id="element-189" data-mp="kpbfahtlz394">
          Three constraints, none of them about the value of the brand.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 !mt-3" data-id="element-190" data-mp="1r5dq732rss2p">
          {ISSUES.map((issue, idx) => {
          const Icon = issue.icon;
          const visible = stage >= idx + 2;
          return <motion.div key={issue.title} initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: visible ? 1 : 0,
            y: visible ? 0 : 16
          }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-5 flex flex-col" data-id="element-191" data-mp="23oitu3u693u4" data-mpi={idx}>
                <div className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-400 flex items-center justify-center mb-4" data-id="element-192" data-mp="2mf0le539ptdw">
                  <Icon className="w-5 h-5" data-id="element-193" data-mp="28maupncso0ag" />
                </div>
                <p className="text-rose-400 text-[11px] font-semibold uppercase tracking-widest mb-1.5" data-id="element-194" data-mp="2co5v8pmwi9n6">
                  {issue.label}
                </p>
                <h3 className="text-white font-semibold text-lg leading-snug mb-3" data-id="element-195" data-mp="30cyryobckliu">
                  {issue.title}
                </h3>
                <p className="text-white/70 text-sm leading-relaxed flex-1" data-id="element-196" data-mp="1hgdpyo98x6t9">
                  {issue.desc}
                </p>
              </motion.div>;
        })}
        </div>
      </div>
    </div>;
}